import { useState } from "react";
import { Lock } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import UpgradeDialog from "@/components/UpgradeDialog";
import { useSubscription } from "@/hooks/useSubscription";

interface LockedContentProps {
  children: React.ReactNode;
  requiredTier: 'adventurer' | 'organizer';
  featureDescription: string;
  blurContent?: boolean;
  className?: string;
}

const tierNames = {
  adventurer: 'Adventurer',
  organizer: 'Organizer',
};

const LockedContent = ({
  children,
  requiredTier,
  featureDescription,
  blurContent = true,
  className = "",
}: LockedContentProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { canAccess, isLoading } = useSubscription();

  // If user has access, just render children
  if (isLoading) {
    return <div className={className}>{children}</div>;
  }

  if (canAccess(requiredTier)) {
    return <>{children}</>;
  }

  // User doesn't have access - show locked state
  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={`relative cursor-pointer group ${className}`}
              onClick={() => setDialogOpen(true)}
            >
              {/* Content with optional blur */}
              <div className={blurContent ? "blur-sm select-none pointer-events-none" : "opacity-50"}>
                {children}
              </div>

              {/* Lock overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-background/60 rounded-lg transition-colors group-hover:bg-background/70">
                <div className="flex flex-col items-center gap-2 text-center p-4">
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                    <Lock className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <span className="text-sm font-medium text-foreground">
                    Upgrade to Unlock
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {tierNames[requiredTier]} members only
                  </span>
                </div>
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>Upgrade to {tierNames[requiredTier]} to {featureDescription}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <UpgradeDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        targetTier={requiredTier}
        featureContext={featureDescription}
      />
    </>
  );
};

export default LockedContent;
